import React from 'react';
import classnames from 'classnames/bind';

import styles from './community.scss';
let cx = classnames.bind(styles);

const CommunityGuidelines = ({ locale, translations }) => {
    return (
        <main className={cx('community', 'flex', 'column')}>
            <div className={cx('title')}>Community Guidelines</div>
            <p>
                Last update: 8 April 2024
            </p>

            <h1>Overview</h1>

            <p>
                The PNK community plays a vital role in making our app a friendly and inclusive environment and we believe this starts with being welcoming and respectful. Our Community Guidelines set the standards and expectations necessary to protect the community and promote an atmosphere of inclusion, sharing and connecting; they outline what is allowed and what is not allowed on PNK. Every new user must accept these guidelines when they sign up to the app.
                <br /><br />
                As you will see below, our Community Guidelines include rules about nudity, violence, drugs, hate speech, fake profiles and bullying. When we become aware that someone violates these guidelines, we investigate and take the appropriate action. We encourage our users to report any concerns to us and our own algorithms also generate reports when they detect a possible breach of our guidelines, so we can take action.
                <br /><br />
                Our Community Guidelines apply to all our users, wherever they are in the world and whether they are posting content on their profile or chatting on PNK. Because things change so fast in the digital world, these guidelines are a living document that we update regularly.
            </p>

            <h1>Our Community Guidelines</h1>

            <h2>Age and identity</h2>

            <p>
                To keep the PNK community authentic and reduce the risk of online abuse, it is vital that our users are who they say they are. We expect everyone to provide real information about themselves at sign-up and we may carry out checks on these personal details to verify their age and identity.
                <br /><br />
                When you set up your PNK profile, you must provide the following information:
                <br /><br />
                <ul>
                    <li>Your date of birth (you must be at least 13 years old to use PNK)</li>
                    <li>Your name</li>
                    <li>The gender you identify with</li>
                    <li>A photo that shows your face clearly</li>
                </ul>
                <br /><br />
                Your PNK account should be yours only (not a joint account that you share with someone else) and should not be used to impersonate someone else.
                <br /><br />
                We verify your age and identity in a number of ways, including:
                <br /><br />
                <ul>
                    <li>Using age estimation tool</li>
                    <li>Offering you the opportunity to have your profile checked (through ID verification or otherwise)</li>
                </ul>
                <br /><br />
                If you provide incorrect personal details (i.e. lie about your age) or pretend to be someone you are not, we may ban you from PNK.
                <br /><br />
                If you are concerned that someone on PNK is not who they say they are, please report it to us using the built-in reporting feature.
            </p>

            <h2>Nudity and sexual content and behaviours</h2>

            <p>
                Certain types of content and behaviours may be inappropriate for our community and put the users that partake in it at risk - especially when this involves minors. As a result, we do not allow content involving nudity, including partial nudity (e.g. images of topless users, users wearing very revealing clothing, underwear, or swimwear out of context). Pornographic, sexual and suggestive content are also prohibited.
                <br /><br />
                If you are concerned about any content or behavior on PNK, please report it to us using the built-in reporting feature.
            </p>

            <h2>Bullying, harassment and other abusive behaviors</h2>

            <p>
                We expect everyone in the PNK community to interact with other users in a respectful manner. Bullying, harassment or any behaviors that could cause harm or make other users feel uncomfortable or unsafe are not tolerated. This includes mockery, name-calling, spreading rumours, sharing personal information or private content, and all other behaviours that could cause intentional harm to others.
                <br /><br />
                Additionally, we do not tolerate sexual harassment and unwanted sexual advances towards other PNK users. This includes any behaviours targeting other users with unwanted sexual behaviours, content, comments and requests, as well as the sharing or threatening to share another user’s private information or intimate images without the consent of this user.
                <br /><br />
                If you are concerned about any content or behaviour on PNK, please report it to us using the built-in reporting feature.
            </p>

            <h2>Discrimination, hate and extremism</h2>

            <p>
                Everybody should be able to feel safe and comfortable to be themselves on PNK. We do not tolerate violent, hateful, discriminatory or abusive behaviors targeted towards another person or group of people on the basis of their race, ethnicity, nationality, religion, gender identity, sexual orientation or a disability or health condition.
                <br /><br />
                We also do not allow the glorification of ideologies or groups that promote hateful and violent ideas - this includes sharing terrorist or extremist propaganda or expression of support for these ideologies, groups and people that represent them, as well as posting symbols and slogans associated with these ideologies.
                <br /><br />
                If you are concerned about any content or behavior on PNK, please report it to us using the built-in reporting feature.
            </p>

            <h2>Violence and graphic content</h2>

            <p>
                Violence has no place within the PNK community. We prohibit behaviours that could result in real life violence, such as threats, incitement to violence or glorification of violence. We will also report to Law Enforcement situations involving an imminent or realistic risk of real life violence. Additionally, we do not allow content that depicts violent acts, weapons or content that is graphic or gory in nature.
                <br /><br />
                If you are concerned about any content or behavior on PNK, please report it to us using the built-in reporting feature.
            </p>

            <h2>Child sexual exploitation and abuse</h2>

            <p>
                In order to keep our young users safe, we take a zero tolerance approach towards child sexual abuse and exploitation and have developed a set of policies and guidelines to proactively identify and respond to these risks. Any behavior that involves or could lead to the sexual abuse and exploitation of minors, whether online or in real life, is prohibited; this includes child sexual abuse, child sexual exploitation and trafficking, grooming, the sharing of unsolicited obscene material with minors, as well as child sexual abuse material. Users engaging in these behaviours will be banned from PNK, and the violation they committed may be reported to the appropriate authorities.
                <br/><br />
                If you are concerned about any content or behaviour on PNK, please report it to us using the built-in reporting feature.
            </p>

            <h2>Privacy Violations</h2>

            <p>
                Every PNK user has the right to privacy; we do not allow members of our community to share or disclose other people’s personal information, such as their full name, home address, phone number, email or social media details, or personal photos and videos without prior permission.
                <br /><br />
                Read our Privacy Policy to learn how we process users’ personal information.
                <br /><br />
                If you are concerned about any content or behavior on PNK, please report it to us using the built-in reporting feature.
            </p>

            <h2>Platform misuse and fraudulent activity</h2>

            <p>
                PNK is a platform to socialize and make new friends; it is not a marketplace. We do not allow users to advertise, offer and promote services and goods in exchange for money or other goods, or collecting funds from other users - especially when it comes to illegal or regulated products and services.
                <br /><br />
                Impersonation, scamming, blackmail, sexual extortion (or “sextortion”), hacking and any attempt to wrongfully access information or money from other users or PNK itself are prohibited. If you engage in illegal behaviors, we may permanently suspend your account and depending on the severity, escalate to Law Enforcement.
                <br /><br />
                If you are concerned about any content or behavior on PNK, please report it to us using the built-in reporting feature.
            </p>

            <h2>Self harm</h2>

            <p>
                Young people may use PNK to seek out friendship and find support when they navigate a difficult time; being able to provide this space is very important to us, but in order to keep our community a safe space for everyone, we may have to take action in situations involving risk of self harm or suicide. Should we become aware that a user intends to harm themselves, we may report the situation to Law Enforcement so they can receive adequate support. Additionally, we do not allow content that depicts, encourages or promotes self-harm, eating disorders, and suicide to other PNK users.
                <br /><br />
                If you are concerned about any content or behavior on PNK, please report it to us using the built-in reporting feature.
            </p>

            <h2>Enforcement Approach</h2>

            <p>
                As a member of the PNK community, you must agree to adhere to our Community Guidelines. If we receive a report of a violation of these guidelines (either from another user or generated by our own algorithms), we investigate and take appropriate action on the basis of our guidelines. Our response will depend on how severe the violation is and the level of risk it poses for other PNK users.
                <br /><br />
                Our moderation actions typically include content removal, access restriction, suspension or permanent ban from our platform.
                <br /><br />
                In instances involving a reporting obligation, potential illegal behaviour on PNK or an emergency situation, we may work with law enforcement and other relevant entities to take additional action.
            </p>

            <br />
            <br />
            <br />
            <br />
            <br />
            <br />
        </main>
    );
};

export default CommunityGuidelines;
